// @flow

import domready from 'domready'
import { createUniqueId } from '@nrk/dh-utils'
import { createGoogleAnalyticsTracker } from '@nrk/dh-analytics-utils'
import { getContextId } from './getContextId'
import { createErrorReporter } from './errorReporting'

const prepareDom = new Promise(resolve => {
  domready(resolve)
})

export type SendGaEvent = (eventObj: {}) => void
export type SendError = (message: any) => void

export type InitFn = (
  el: HTMLElement,
  {
    sendGaEvent: SendGaEvent,
    sendError: SendError
  }
) => void

// eslint-disable-next-line
declare var __webpack_public_path__: any

function setStaticHost(el) {
  const staticHost = el && el.getAttribute('data-static-host')

  if (staticHost) {
    // eslint-disable-next-line
    __webpack_public_path__ = `${staticHost}/`
  }
}

export function mount(
  rootElementClassName: string,
  initFn: InitFn,
  loadPolyfills?: () => Promise<void>,
  featureTests?: () => { [key: string]: string }
) {
  const els = findElements(rootElementClassName)
  setStaticHost(els[0])
  Promise.all([prepareDom, loadPolyfills && loadPolyfills()]).then(res => {
    const polyfills = res[1]
    window[rootElementClassName] = window[rootElementClassName] || {}

    els.forEach(el => {
      el.classList.remove('no-js')
      el.id = createUniqueId()
      window[rootElementClassName][el.id] = true

      let featureTestResult = {}
      if (featureTests) {
        featureTestResult = applyFeatureClasses(el, featureTests)
      }

      const name = el.getAttribute('data-name')

      const sendGaEvent = setupGA(el, name || '')
      sendGaEvent({ action: 'MOUNT' })
      const sendError = setupErrorTracking(el, name || '')
      initFn(el, { sendGaEvent, sendError, polyfills, featureTests: featureTestResult })
    })
  })
}

function setupGA(el, name: string): SendGaEvent {
  const gaPropertyId = el.getAttribute('data-ga-property-id')
  const metadata = el.getAttribute('data-metadata')
  const { title, selfId, published } = JSON.parse(
    metadata && metadata !== 'undefined' ? metadata : '{}'
  )

  const googleAnalyticsTracker = createGoogleAnalyticsTracker({
    name: 'dh',
    gaPropertyId,
    self: {
      id: selfId,
      title,
      publishedDate: published ? new Date(published) : null
    },
    context: {
      id: getContextId(document.location.href)
    }
  })
  const sendGaEvent = eventObj => {
    googleAnalyticsTracker.sendEvent({
      category: `dh-modul-${name}`,
      ...eventObj
    })
  }
  return sendGaEvent
}

function setupErrorTracking(el: HTMLElement, name: string): SendError {
  const errorTrackingEnabled = el.getAttribute('data-error-tracking-enabled') === 'true'
  const simpleErrorReport = createErrorReporter(name, errorTrackingEnabled)
  simpleErrorReport.listenAndCatch()

  const sendError = message => {
    simpleErrorReport.sendErrorReport({ message })
  }

  return sendError
}

function applyFeatureClasses(el: Element, testFn: () => { [key: string]: string }) {
  const result = testFn()
  Object.values(result)
    .filter(Boolean)
    .forEach(className => el.classList.add(String(className)))

  return result
}

function findElements(className) {
  return [].slice
    .call(document.getElementsByClassName(className))
    .filter(el => !isInitialized(el, className))
}

function isInitialized(el, className) {
  return el.id && window[className][el.id]
}
